/**
 * Relationship mapping utility
 * Maps template keys to their English relationship names
 * Used across the application for consistent relationship name formatting
 */

export const RELATIONSHIP_MAP = {
  "child": "child",
  "spouse": "spouse", 
  "other-person": "other",
  "other": "other",
  "pet": "pet",
  "non-profit": "non-profit",
  "other-beneficiary": "other",
  "heirloom": "heirloom",
  "handshake-debt-owed-you": "debt owed to you",
  "vehicle": "vehicle",
  "bank-account": "bank account",
  "life-insurance": "life insurance",
  "jewelry": "jewelry",
  "private-business": "private business",
  "property": "property",
  "retirement-account": "retirement account",
  "cryptocurrency": "cryptocurrency"
};

// Portuguese to English mapping for direct relationship names
export const PORTUGUESE_TO_ENGLISH_MAP = {
  "cônjuge": "spouse",
  "filho(a)": "child",
  "outra Pessoa": "other",
  "outro beneficiário": "other",
  "other person": "other",
  "other beneficiary": "other",
  "pet": "pet",
  "animal de estimação": "pet",
  "sem fins lucrativos": "non-profit",
  "instituição de caridade": "non-profit",
  "joia": "jewelry",
  "veículo": "vehicle",
  "conta bancária": "bank-account",
  "seguro de vida": "life-insurance",
  "negócio privado": "private-business",
  "propriedade": "property",
  "conta de aposentadoria": "retirement-account",
  "aposentadoria": "retirement-account",
  "criptomoeda": "cryptocurrency",
  "herança": "heirloom",
  "dívida a receber": "handshake-debt-owed-you"
};

/**
 * Maps Portuguese relationship names directly to English template keys
 * @param {string} relationshipName - The relationship name (e.g., "cônjuge", "filho")
 * @returns {string} The English template key (e.g., "spouse", "child")
 */
export const mapPortugueseToEnglish = (relationshipName) => {
  if (!relationshipName) return "";
  
  // Convert to lowercase for matching
  const lowerName = relationshipName.toLowerCase().trim();
  
  // Check if it's already in English or in our mapping
  return PORTUGUESE_TO_ENGLISH_MAP[lowerName] || relationshipName;
};

/**
 * Gets the relationship name from a template key
 * @param {string} templateKey - The template key (e.g., "spouse", "child")
 * @returns {string} The formatted relationship name
 */
export const getRelationshipName = (templateKey) => {
  return RELATIONSHIP_MAP[templateKey] || templateKey;
};

/**
 * Extracts template key from full path and returns relationship name
 * @param {string} fullPath - Full path like "templates.spouse.name"
 * @returns {string} The relationship name
 */
export const getRelationshipFromPath = (fullPath) => {
  if (!fullPath) return "";
    
  // Extract just the key part from the full path like "templates.spouse.name"
  const templateKey = fullPath.includes('.') ? fullPath.split('.')[1] : fullPath;
  
  const result = getRelationshipName(templateKey);
  
  return result;
};

/**
 * Extracts template key from object containing slug or dataTemplateName
 * @param {Object} obj - Object with dataTemplateSlug and/or dataTemplateName
 * @returns {string} The extracted template key (e.g., "spouse", "child", "other-person")
 */
export const extractTemplateKey = (obj) => {
  if (!obj) return "";
  
  const slug = obj.dataTemplateSlug || "";
  const templateName = obj.dataTemplateName || "";
  
  // Extract the key from slug or dataTemplateName path (e.g. "templates.other-person.name" → "other-person")
  const templateKey = slug || (templateName.includes('.') ? templateName.split('.')[1] : templateName);
  
  return templateKey;
};

/**
 * Maps a tag (translation key or translated text) to the API relationship format
 * Handles both English and Portuguese tags, translation keys, and direct text
 * @param {string} tag - The tag value (e.g., "templates.spouse.name", "Spouse", "Cônjuge", "Other Person")
 * @returns {string} The template key for i18n (e.g., "spouse", "child", "other-person")
 */
export const mapTagToRelationship = (tag) => {
  if (!tag) return "other";
  
  const tagLower = tag.toLowerCase().trim();
  
  // Check if it's a translation key format (e.g., "templates.spouse.name")
  if (tagLower.includes("templates.")) {
    const parts = tagLower.split(".");
    if (parts.length >= 2) {
      const templateKey = parts[1]; // Extract "spouse" from "templates.spouse.name"
      return RELATIONSHIP_MAP[templateKey] || "other";
    }
  }
  
  // Check Portuguese to English mapping
  const englishEquivalent = PORTUGUESE_TO_ENGLISH_MAP[tagLower];
  if (englishEquivalent) {
    return englishEquivalent;
  }
  
  // Check if it's already a valid relationship key
  if (RELATIONSHIP_MAP[tagLower]) {
    return RELATIONSHIP_MAP[tagLower];
  }
  
  // Handle common variations
  if (tagLower.includes("child") || tagLower.includes("filho")) {
    return "child";
  }
  if (tagLower.includes("spouse") || tagLower.includes("cônjuge")) {
    return "spouse";
  }
  if (tagLower.includes("other") || tagLower.includes("outra") || tagLower.includes("outro")) {
    // Distinguish between other-person and other-beneficiary
    if (tagLower.includes("beneficiary") || tagLower.includes("beneficiário")) {
      return "other-beneficiary";
    }
    return "other";
  }
  if (tagLower.includes("pet")) {
    return "pet";
  }
  if (tagLower.includes("non-profit") || tagLower.includes("nonprofit") || tagLower.includes("sem fins") || tagLower.includes("caridade")) {
    return "non-profit";
  }
  
  // Default fallback
  return "other";
};

export default {
  RELATIONSHIP_MAP,
  PORTUGUESE_TO_ENGLISH_MAP,
  getRelationshipName,
  getRelationshipFromPath,
  extractTemplateKey,
  mapTagToRelationship,
};
